import { useEffect, useRef, type FormEvent, type KeyboardEvent } from 'react';
import { Send, X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * C20-138 — Chat input. Textarea with auto-grow, submit-on-Enter
 * (Shift+Enter inserts a newline), disabled while pending, and an
 * optional cancel button shown only when `pending` is true.
 *
 * Auto-grow: we measure scrollHeight on each value change and resize
 * up to the prototype's max-height (120px). Below that the textarea
 * keeps its natural min-height (24px in the prototype).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-engagement.js`
 * lines 593-604 — the input is a paper-0 panel with `border-radius:8px`,
 * a borderless textarea, and the send button on the trailing edge.
 *
 * The prototype's paperclip "Attach scope" button is left out until
 * attaching a scope works: a control that can only be disabled, with a
 * "pending" label, told users about our backlog rather than their task
 * (C2-32).
 *
 * Accessibility:
 *   - The textarea is labeled via `aria-label`.
 *   - The form is submitted by both Enter (handled in keydown) and the
 *     send button (native form submit).
 *   - Disabled is set on every input while pending so screen readers
 *     announce the right state.
 */
export interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  /** When true, all controls are disabled and the cancel button shows. */
  pending?: boolean;
  /** Called when the user clicks Cancel (only rendered when pending). */
  onCancel?: () => void;
  placeholder?: string;
  className?: string;
}

const DEFAULT_PLACEHOLDER =
  "Ask anything about this engagement's documents. Answers cite the source.";
const MAX_HEIGHT_PX = 120;
const MIN_HEIGHT_PX = 24;

export function ChatInput({
  value,
  onChange,
  onSubmit,
  pending,
  onCancel,
  placeholder = DEFAULT_PLACEHOLDER,
  className,
}: ChatInputProps) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  // Auto-grow: clamp scrollHeight between min/max each time the value
  // (or the pending toggle, which can clear the field) changes.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(Math.max(el.scrollHeight, MIN_HEIGHT_PX), MAX_HEIGHT_PX);
    el.style.height = `${next}px`;
  }, [value]);

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Shift+Enter inserts a newline (default browser behavior).
    // Plain Enter submits.
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      if (pending) return;
      const trimmed = value.trim();
      if (trimmed.length === 0) return;
      onSubmit(trimmed);
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    onSubmit(trimmed);
  }

  return (
    <form onSubmit={handleSubmit} className={cn('w-full', className)}>
      <div
        className={cn(
          // Outer panel — paper-0 fill, ink-200 hairline, 8-px radius
          // per the prototype's input styling.
          'flex items-end gap-2.5 rounded-card border border-ink-200 bg-paper-0 px-3 py-2',
          'focus-within:border-accent-amber-600 focus-within:ring-1 focus-within:ring-accent-amber-600',
          // Density tweak: collapse to ink-300 when disabled so the
          // user sees the surface is inert.
          pending && 'opacity-80',
        )}
      >
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={pending}
          aria-label="Ask a question about this engagement"
          rows={1}
          // No border, no resize handle — auto-grow does the work.
          className={cn(
            'flex-1 resize-none border-0 bg-transparent text-body leading-[20px] text-ink-900',
            'outline-none placeholder:text-ink-500',
            'disabled:cursor-not-allowed',
          )}
        />
        {pending && onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-2.5',
              'text-[12px] font-medium text-ink-700',
              'hover:bg-paper-100',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
            aria-label="Cancel pending response"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Cancel</span>
          </button>
        ) : null}
        <button
          type="submit"
          disabled={pending || value.trim().length === 0}
          className={cn(
            // Avero-orange primary button, sm size, send icon + label.
            'inline-flex h-8 items-center gap-1.5 rounded-card px-3',
            'bg-accent-amber-600 text-[12px] font-semibold text-paper-0',
            'hover:bg-accent-amber-700',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          aria-label="Send question"
        >
          <Send className="h-3.5 w-3.5" aria-hidden="true" />
          <span>Send</span>
        </button>
      </div>
    </form>
  );
}
